import React, { useRef, useState } from "react";
import { useLoader } from "../../utils/LoaderUtils";
import { showError, showSuccess } from "../../utils/toastUtils";
import slugify from "slugify";
import blogsAPI from "../../api/BlogApi";
import SunEditor from "suneditor-react";
import ImageUploader from "../../components/partials/ImageUploader";
import SEOFields from "../../components/partials/SEOFields";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SubmitButton from "../../components/partials/global/SubmitButton";

function AddNew() {
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);

  const INITIAL_FORM_DATA = {
    title: "",
    shortDescription: "",
    description: "",
    slug: "",
    date: "",
    author: "",
    image: "",
  };

  const SEO_FORM_DATA = {
    metaTitle: "",
    metaDescription: "",
    tags: [],
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [seoData, setSeoData] = useState(SEO_FORM_DATA);

  const editorRef = useRef(null);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!formData.title) {
      newErrors.title = "Title field is mandatory";
    }

    if (!formData.date) {
      newErrors.date = "Date field is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));

    setErrors((prev) => ({ ...prev, description: "" }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (!validate()) {
    //   return;
    // }

    setIsLoading(true);
    showLoader();

    try {
      const payload = {
        ...formData,
        slug: slugify(formData.title, { lower: true }),
        metaTitle: seoData.metaTitle,
        metaDescription: seoData.metaDescription,
        keywords: seoData.tags.join(","),
      };

      const res = await blogsAPI.addDetails(payload);

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
        setSeoData(SEO_FORM_DATA);
        setErrors({});
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Add New Blog</h4>
            <h6>
              Create a new blog to showcase product listing on your store.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Details</h4>
          </div>

          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="blogs"
              />

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-pencil"></i>
                    </span>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      placeholder="Title"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label">
                    Date <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-calendar"></i>
                    </span>
                    <input
                      type="date"
                      name="date"
                      className="form-control"
                      placeholder="Title"
                      value={formData.date}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.date && (
                    <span className="errors text-danger">{errors.date}</span>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Author</label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-user"></i>
                    </span>
                    <input
                      type="text"
                      name="author"
                      className="form-control"
                      placeholder="Author"
                      value={formData.author}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Short Description</label>
                  <textarea
                    className="form-control"
                    name="shortDescription"
                    placeholder="Short Description"
                    value={formData.shortDescription}
                    onChange={handleChange}
                  ></textarea>
                  {errors.shortdescription && (
                    <span className="errors text-danger">
                      {errors.shortdescription}
                    </span>
                  )}
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>

                  <SunEditor
                    getSunEditorInstance={(sunEditor) => {
                      editorRef.current = sunEditor;
                    }}
                    height="250px"
                    setOptions={{
                      buttonList: [
                        ["undo", "redo"],
                        ["font", "fontSize"],
                        ["formatBlock"],
                        ["bold", "underline", "italic", "strike"],
                        ["fontColor", "hiliteColor"],
                        ["align", "list", "table"],
                        ["link", "image", "video"],
                        ["fullScreen", "preview", "print"],
                        ["codeView"],
                      ],
                      allowedClassNames: ".*", // sab classes allow
                      attributesWhitelist: {
                        all: "style|class|id",
                      },
                      addTagsWhitelist: ".*", // extra tags remove na kare
                      imageFileInput: true,
                      font: [
                        "Arial",
                        "Comic Sans MS",
                        "Courier New",
                        "Georgia",
                        "Tahoma",
                        "Trebuchet MS",
                        "Verdana",
                      ],
                      fontSize: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36], // include 12px
                    }}
                    setContents={formData.description}
                    onChange={handleEditorChange}
                  />
                  {errors.description && (
                    <span className="errors text-danger">
                      {errors.description}
                    </span>
                  )}
                </div>
              </div>

              <hr />

              <SEOFields
                seoData={seoData}
                setSeoData={setSeoData}
                slug={slugify(formData.title || "", { lower: true })}
                errors={errors}
              />

              <div className="row mb-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Submit Details"
                    loadingText="Submitting..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
